import { useQuery } from '@apollo/client';
import { GeoJsonLayer } from '@deck.gl/layers/typed';
import { Box, DialogActions, Skeleton, styled } from '@mui/material';
import {
  centroid,
  circle,
  Feature,
  FeatureCollection,
  featureCollection,
  geometry,
  multiPolygon,
  MultiPolygon,
  point,
  Point,
  polygon,
  Polygon,
} from '@turf/turf';
import { IFeatureProperties } from 'components/FarmPlots/FarmPlots';
import PlotInfoPopup from 'components/FarmPlots/PlotInfoPopup';
import { FlexBox } from 'components/Structure';
import { useDialog, useMessages } from 'components/hooks';
import { IPlotsValues, plotsSchema } from 'constants/schemas/sites.schema';
import { DialogDefault, ThemeButton, ThemeTypography } from 'designSystem';
import Image from 'designSystem/DataDisplay/Image/Image';
import GeoDataField from 'designSystem/Formik/GeoDataField/GeoDataField';
import { ICustomMapRef, IMapPopup } from 'designSystem/Map/CustomMap';
import { calculateArea, radiusFromHectares } from 'designSystem/Map/utils/map.utils';
import Icon from 'designSystem/Primitives/Icon/Icon';
import { Field, Form, Formik, FormikProps } from 'formik';
import { GET_NEIGHBOURING_FARM_PLOTS, GET_PLOTS } from 'graphql/secure/queries/plot.queries';
import {
  INeighbouringFarmPlotsInput,
  IPlotsFilters,
} from 'graphql/secure/queries/types/plot-query.types';
import usePlotsMutations from 'hooks/usePlotMutations';
import isEqual from 'lodash/isEqual';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import theme from 'styles/theme';
import { IDefaultDialogProps } from 'types/dialog.types';
import { IBasePlot, IPlot, TGeometry } from 'types/plot.types';
import { ESiteType, IFarmSite } from 'types/site.types';
import { GraphQlConnection } from 'types/types';
import { convertHexToRGBarray } from 'utils';
import { removeGraphConnections } from 'utils/graphql.utils';
import { SiteTypeLabels } from 'utils/site.utils';
import { v4 as uuid } from 'uuid';

interface IAddEditPlotsDialogProps extends IDefaultDialogProps {
  farm: IFarmSite;
  /**
   * Dataset ID to be used to only show neighboring plots that are in the same dataset
   */
  datasetId?: string;
}

const TitleContainer = styled(FlexBox)(({ theme }) => ({
  width: '100%',
  padding: '8px',
  alignItems: 'center',
  borderRadius: 6,
  gap: theme.spacing(1),
  backgroundColor: theme.custom.themeColors.primary[5],
  marginBottom: theme.spacing(1),
}));

/**
 * If the site plots already present they will be used like they are
 * Otherwise the plots will be queried from the farm
 */
const AddEditPlotsDialog: FC<IAddEditPlotsDialogProps> = ({ open, farm, datasetId, onClose }) => {
  const { data, loading } = useQuery<
    { plots: GraphQlConnection<IPlot> },
    { filters?: IPlotsFilters }
  >(GET_PLOTS, {
    variables: { filters: { farmIds: [farm.id] } },
    skip: !farm.id,
  });
  const { data: neighbouringFarmData, loading: neighbouringFarmPlotsLoading } = useQuery<
    { neighbouringFarmPlots: GraphQlConnection<IPlot> },
    { input?: INeighbouringFarmPlotsInput }
  >(GET_NEIGHBOURING_FARM_PLOTS, {
    variables: { input: { datasetId, farmId: farm.id } },
    skip: !farm.id && !datasetId,
  });

  const { setErrorMessage, setSuccessMessage } = useMessages();
  const { createPlots, deletePlots, updatePlots, loading: mutationLoading } = usePlotsMutations();
  const { openDialog, closeDialog } = useDialog();
  const formRef = useRef<FormikProps<IPlotsValues> | null>(null);
  const customMapRef = useRef<ICustomMapRef>(null);

  const [infoPopup, setInfoPopup] = useState<IMapPopup>();

  const plots: IPlot[] = useMemo(() => {
    if (data?.plots) {
      return removeGraphConnections(data.plots);
    }
    return [];
  }, [data]);

  const neighbouringFarmPlots: IPlot[] = useMemo(() => {
    if (neighbouringFarmData?.neighbouringFarmPlots) {
      return removeGraphConnections(neighbouringFarmData.neighbouringFarmPlots);
    }
    return [];
  }, [neighbouringFarmData]);

  const toMapFeature: (
    cultivatedArea: IBasePlot
  ) => Feature<Polygon | MultiPolygon | Point, { plotId?: string }> = cultivatedArea => {
    const geometry = cultivatedArea.feature.geometry;
    switch (geometry.type) {
      case 'Point':
        return point(geometry.coordinates, { plotId: cultivatedArea.id }, { id: uuid() });
      case 'Polygon':
        return polygon(geometry.coordinates, { plotId: cultivatedArea.id }, { id: uuid() });
      case 'MultiPolygon':
        return multiPolygon(geometry.coordinates, { plotId: cultivatedArea.id }, { id: uuid() });
      default:
        throw Error('Invalid geometry type');
    }
  };

  const initialValues: IPlotsValues = useMemo(
    () => ({
      ...plotsSchema.default(),
      ...(plots.length
        ? {
            featureCollection: featureCollection<
              Polygon | MultiPolygon | Point,
              { plotId?: string }
            >(plots.map(plot => toMapFeature(plot))),
          }
        : {
            featureCollection: featureCollection<
              Polygon | MultiPolygon | Point,
              { plotId?: string }
            >([]),
          }),
    }),
    [plots]
  );

  /**
   * This feature collections contains all the cultivation areas of the farm as single polygons with their properties
   * To be able to show the info popup when clicking on the cultivation area on a single polygon we need to have the center of each polygon
   * To be able to show centered markers of the one cultivation area we need to have the center of the multi polygon
   */
  const neighbouringPolygonFeatures: FeatureCollection<
    Polygon | Point | MultiPolygon,
    IFeatureProperties
  > = useMemo(() => {
    const features: Feature<Polygon | Point | MultiPolygon, IFeatureProperties>[] =
      neighbouringFarmPlots.map(
        ({
          id: plotId,
          farmId,
          farm: { locationName, title, rawMaterial, ownedBy },
          feature,
          sizeHa,
        }) => {
          const featureGeometry = geometry(feature.geometry.type, feature.geometry.coordinates);
          const properties: IFeatureProperties = {
            plotId,
            farmId,
            name: locationName,
            title,
            outputTitle: rawMaterial?.title,
            ownedBy,
            areaSizeHa: sizeHa || calculateArea(featureGeometry),
            // Center of the polygon always inside the polygon
            polygonCenter:
              feature.geometry.type === 'Point'
                ? feature.geometry.coordinates
                : (centroid(featureGeometry).geometry.coordinates as [number, number]),
          };
          if (feature.geometry.type === 'Polygon') {
            return polygon(feature.geometry.coordinates, properties, { id: uuid() });
          } else if (feature.geometry.type === 'MultiPolygon') {
            return multiPolygon(feature.geometry.coordinates, properties, { id: uuid() });
          } else if (feature.geometry.type === 'Point') {
            return circle(
              feature.geometry.coordinates,
              // For now we use a circle with a radius of 112.8 meters which has 4ha area
              sizeHa ? radiusFromHectares(sizeHa) : 112.8,
              {
                units: 'meters',
                properties,
              }
            );
          }
          throw new Error(
            `Cultivation area ${farmId} has an invalid geometry type: ${feature.geometry}`
          );
        }
      );
    return featureCollection(features);
  }, [neighbouringFarmPlots]);

  const handleClose = () => {
    if (formRef.current && !isEqual(formRef.current.values, initialValues)) {
      openDialog({
        type: 'ALERT',
        props: {
          title: 'Unsaved changes',
          text: 'Are you sure you want to close this window? All unsaved changes will be lost and you will not be able to undo this action.',
          submitText: 'Close',
          itemTitle: farm.title,
          displayCloseButton: true,
          onSubmit: onClose,
          onCancel: () => undefined,
        },
      });
    } else {
      onClose?.();
    }
  };

  const handleEditFarm = useCallback(
    (farmId: string) => {
      // Make sure the map is not in fullscreen mode anymore
      customMapRef.current?.exitFullscreen();

      // Close all open popups
      closeDialog();
      setInfoPopup(undefined);

      const farm: IFarmSite | undefined = neighbouringFarmPlots.find(
        ({ farm }) => farm.id === farmId
      )?.farm;
      if (!farm) {
        throw new Error('Could not find farm');
      }
      openDialog({
        type: 'ADD_EDIT_SITE',
        props: { site: farm },
      });
    },
    [neighbouringFarmPlots, openDialog, closeDialog]
  );

  const handleEditFarmPlots = useCallback(
    (farmId: string) => {
      // Make sure the map is not in fullscreen mode anymore
      customMapRef.current?.exitFullscreen();

      // Close all open popups
      closeDialog();
      setInfoPopup(undefined);

      const farm: IFarmSite | undefined = neighbouringFarmPlots.find(
        ({ farm }) => farm.id === farmId
      )?.farm;
      if (!farm) {
        throw new Error('Could not find farm');
      }
      openDialog({
        type: 'ADD_EDIT_PLOTS',
        props: {
          farm,
          datasetId,
        },
      });
    },
    [neighbouringFarmPlots, datasetId, openDialog, closeDialog]
  );

  const handleFeatureClick = useCallback(
    (featureProperties: IFeatureProperties) => {
      setInfoPopup(prev => {
        if (prev && prev.id === featureProperties.plotId) {
          return undefined;
        }
        return {
          id: featureProperties.plotId,
          coordinate: featureProperties.polygonCenter,
          content: (
            <PlotInfoPopup
              infoItem={featureProperties}
              onEditFarmPlotsClick={handleEditFarmPlots}
              onEditFarmClick={handleEditFarm}
            />
          ),
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [infoPopup, setInfoPopup]
  );

  const neighbouringLayer = useMemo(
    () =>
      new GeoJsonLayer({
        id: 'neighbouring-plots-map-layer',
        data: neighbouringPolygonFeatures,
        getLineWidth: 2,
        pickable: true,
        getFillColor: () => convertHexToRGBarray(theme.custom.themeColors.grayScale[40], 60),
        getLineColor: () => convertHexToRGBarray(theme.custom.themeColors.grayScale[40]),
        getPointRadius: 4,
        onClick: info => handleFeatureClick(info.object.properties),
      }),
    [neighbouringPolygonFeatures, handleFeatureClick]
  );

  const handleDeleteAllPlots = async () => {
    await deletePlots({
      variables: { ids: plots.map(({ id }) => id) },
      onCompleted: () => {
        setSuccessMessage('Cultivation areas successfully deleted');
      },
      onError: () => {
        setErrorMessage('Failed to delete cultivation areas');
      },
    });
    onClose?.();
  };

  /**
   * Delete all plots
   */
  const handleDeleteCultivatedArea = async () => {
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete all cultivation areas of',
        text: 'Are you sure you want to delete all cultivation areas? All data will be lost and you will not be able to recover the plots.',
        submitText: 'Delete',
        itemTitle: farm.title,
        displayCloseButton: true,
        onSubmit: handleDeleteAllPlots,
        onCancel: () => undefined,
      },
    });
  };

  /**
   * Handle all changes from the initial values to the new values of the form
   * All three mutation delete / update / create can be triggered here based on the changes
   */
  const handleSubmit = async (values: IPlotsValues) => {
    try {
      const newFeatures = values.featureCollection.features;
      const plotsToCreate = newFeatures.filter(({ properties }) => !properties?.plotId);
      const plotsToUpdate = newFeatures.filter(({ geometry, properties }) => {
        if (!properties?.plotId) return false;
        const prevPlot = plots.find(({ id }) => id === properties.plotId);
        if (!prevPlot) return false;
        return !isEqual(geometry, prevPlot.feature.geometry);
      }) as Feature<Polygon | MultiPolygon | Point, { plotId: string }>[]; // All features without a plotId got filtered out
      const plotIdsToDelete = plots
        .filter(({ id }) => !newFeatures.some(({ properties }) => properties?.plotId === id))
        .map(({ id }) => id);
      const mutationArray = [];
      // Check if there are any plots to create, if not we don't need to call the createPlots mutation
      if (plotsToCreate.length) {
        mutationArray.push(
          createPlots({
            variables: {
              inputs: plotsToCreate.map(({ geometry }) => ({
                farmId: farm.id,
                // Can not automatically transform turf coordinates number[] into [number, number]
                geometry: geometry as TGeometry,
              })),
            },
          })
        );
      }
      if (plotsToUpdate.length) {
        mutationArray.push(
          updatePlots({
            variables: {
              inputs: plotsToUpdate.map(({ geometry, properties }) => ({
                id: properties?.plotId,
                // Can not automatically transform turf coordinates number[] into [number, number]
                geometry: geometry as TGeometry,
              })),
            },
          })
        );
      }
      if (plotIdsToDelete.length) {
        mutationArray.push(
          deletePlots({
            variables: { ids: plotIdsToDelete },
          })
        );
      }
      // Resolve all promises and check if all mutations were successful
      const result = await Promise.all(mutationArray);
      if (result) {
        setSuccessMessage('Cultivation areas successfully saved');
        onClose?.();
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('An unknown error occurred while saving the cultivation areas.');
    } finally {
      formRef.current?.setSubmitting(false);
    }
  };

  return (
    <DialogDefault
      title="Edit cultivation area"
      onClose={handleClose}
      data-cy="add-edit-activity-dialog"
      open={open}
      fullWidth
      iconName="polygon-mapping"
      maxWidth="md"
    >
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        validateOnChange
        enableReinitialize
        validationSchema={plotsSchema}
        onSubmit={handleSubmit}
      >
        {({ values, isSubmitting, isValid }) => (
          <Form style={{ width: '100%' }}>
            <Box
              display="flex"
              flexDirection="column"
              p={3}
              height="60vh"
              minWidth={600}
              width="100%"
              gap={2}
            >
              <TitleContainer>
                <Box display="flex" bgcolor="white" borderRadius="6px" padding="4px">
                  {!!farm.image ? (
                    <Image width={44} height={44} image={farm.image} />
                  ) : (
                    <Box display="flex" bgcolor="white" borderRadius="6px" padding="4px">
                      <Icon name="site-placeholder" size="xx-large" color="blue-ice" />
                    </Box>
                  )}
                </Box>

                <Box width="calc(100% - 60px)">
                  <FlexBox>
                    <ThemeTypography variant="ITEM_TITLE" autoOverflow>
                      {farm.title}
                    </ThemeTypography>
                    {farm.externalId && (
                      <Box ml={0.8} maxWidth="30%">
                        <ThemeTypography variant="BODY_MEDIUM" autoOverflow color="GRAY_80">
                          | {farm.externalId}
                        </ThemeTypography>
                      </Box>
                    )}
                  </FlexBox>

                  <FlexBox>
                    <Icon name="tag-filled" size="small" color="gray-60" />
                    <Box ml={0.5} />
                    <ThemeTypography variant="LABEL_INPUT">
                      {SiteTypeLabels[farm.siteType]}
                    </ThemeTypography>
                    <Box ml={1} />
                    {farm.siteType === ESiteType.FARM && (
                      <>
                        <Icon name="raw-material" size="small" color="gray-60" />
                        <Box ml={0.5} />
                        <ThemeTypography variant="LABEL_INPUT" autoOverflow>
                          {farm.rawMaterial?.title}
                        </ThemeTypography>
                        <Box ml={1} />
                      </>
                    )}
                    <FlexBox gap={0.5}>
                      <Icon name="location" size="small" color="gray-60" />
                      <Icon name="site-location-point" size="small" color="light-violet" />
                      <Box />
                      <ThemeTypography variant="LABEL_INPUT" autoOverflow maxWidth={200}>
                        {farm.locationName}
                      </ThemeTypography>
                    </FlexBox>
                  </FlexBox>
                </Box>
              </TitleContainer>

              {/* We can only show the map if all plots are loaded */}
              {loading || neighbouringFarmPlotsLoading ? (
                <Box height="100%" display="flex" flexDirection="column" gap={2}>
                  <Skeleton variant="rounded" width={250} height={26} />
                  <Skeleton variant="rounded" width={350} height={10} />
                  <Skeleton variant="rounded" width="100%" height={'calc(100% - 64px)'} />
                </Box>
              ) : (
                <Field
                  name="featureCollection"
                  infoPopup={infoPopup}
                  customMapRef={customMapRef}
                  defaultMapLayers={[neighbouringLayer]}
                  component={GeoDataField}
                  marker={farm.locationCoordinates}
                  defaultCenter={{ ...farm.locationCoordinates }}
                  onMarkerPopupClose={() => setInfoPopup(undefined)}
                />
              )}
            </Box>

            <DialogActions>
              <FlexBox width="100%" justifyContent="space-between">
                <ThemeButton
                  color="BLUE_ICE"
                  size="large"
                  type="button"
                  loading={
                    loading || neighbouringFarmPlotsLoading || mutationLoading || isSubmitting
                  }
                  onClick={handleDeleteCultivatedArea}
                >
                  Delete all plots
                </ThemeButton>

                <FlexBox gap={2}>
                  <ThemeButton color="BLUE_ICE" size="large" type="button" onClick={handleClose}>
                    Cancel
                  </ThemeButton>

                  <ThemeButton
                    data-cy="submit-activity-form-btn"
                    loading={
                      loading || neighbouringFarmPlotsLoading || mutationLoading || isSubmitting
                    }
                    color="YELLOW"
                    size="large"
                    type="submit"
                    disabled={!isValid || isEqual(values, initialValues)}
                  >
                    Save
                  </ThemeButton>
                </FlexBox>
              </FlexBox>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </DialogDefault>
  );
};

export default AddEditPlotsDialog;
